import { Avatar, Button, Dropdown } from '@gymcoach/ui';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { ROLE_LABELS } from '@/auth/roles';
import { useMe } from '@/auth/useMe';
import { NotificationBell } from '@/notifications/NotificationBell';

import { Breadcrumbs } from './Breadcrumbs';
import { GymMark } from './GymMark';
import { useTheme } from './theme';
import { useGymLogoUrl } from './useGymLogoUrl';

/**
 * The bar above every screen (ported from legalcrm): menu button on phones, breadcrumbs,
 * and the account menu. The notification bell arrives with M13 and sits left of the
 * account menu.
 */
export function TopBar({ onOpenSidebar }: { onOpenSidebar: () => void }) {
  const { signOut } = useAuth();
  const { profile, role, gym } = useMe();
  const logoUrl = useGymLogoUrl(gym.logo_path);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-surface/95 px-4 backdrop-blur">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onOpenSidebar} aria-label="Open navigation">
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </Button>

      {/* On a phone the sidebar is hidden: the gym's name stands in for it here. */}
      <GymMark name={gym.name} logoUrl={logoUrl} className="lg:hidden" />
      <Breadcrumbs className="hidden min-w-0 flex-1 lg:flex" />

      <div className="ml-auto flex items-center gap-1">
        <NotificationBell />
        <Dropdown
          label="Account"
          trigger={() => (
            <span className="flex items-center gap-2 rounded p-1 hover:bg-surface-sunken">
              <Avatar name={profile.full_name} size="sm" />
              <span className="hidden text-left sm:block">
                <span className="block max-w-[10rem] truncate text-sm font-medium text-ink">{profile.full_name}</span>
                <span className="block text-xs text-ink-muted">{ROLE_LABELS[role]}</span>
              </span>
            </span>
          )}
          items={[
            {
              key: 'profile',
              label: profile.email ?? 'My account',
              onSelect: () => navigate('/account'),
            },
            {
              key: 'theme',
              label: theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
              onSelect: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
              separatorBefore: true,
            },
            {
              key: 'signout',
              label: 'Sign out',
              destructive: true,
              separatorBefore: true,
              onSelect: () => {
                void signOut().then(() => navigate('/login'));
              },
            },
          ]}
        />
      </div>
    </header>
  );
}
